/**
 * Pagination Django `sessions/etudiants_bis/` — agrégation complète côté client.
 * Aligné sur aggregatePaginatedDjangoResults (Laravel) : suit `next` jusqu'à épuisement ou count atteint.
 */

export const ETUDIANTS_BIS_PAGE_SIZE = 100

export const ETUDIANTS_BIS_TRUNCATED_MESSAGE =
	'La liste des étudiants est incomplète (pagination interrompue). Certaines données peuvent manquer.'

/** Affiche un toast si la pagination etudiants_bis n'a pas pu être complétée. */
export function warnEtudiantsBisTruncated(truncated: boolean, context?: string): void {
	if (!truncated) return
	const suffix = context ? ` (${context})` : ''
	void import('@/utils/toastSafe')
		.then((m) => m.toastSafe.warning(`${ETUDIANTS_BIS_TRUNCATED_MESSAGE}${suffix}`))
		.catch(() => {})
}

export type EtudiantsBisPagedResponse = {
	results?: any[]
	count?: number
	next?: string | null
	previous?: string | null
	nb_licence?: number
	nb_master?: number
}

export type EtudiantsBisFetcher = (
	params: Record<string, unknown>,
	showLoading?: boolean
) => Promise<EtudiantsBisPagedResponse>

export interface FetchAllEtudiantsBisOptions {
	perPage?: number
	/** Garde-fou optionnel (0 = illimité, comme le backend Laravel). */
	maxPages?: number
	useloading?: boolean
}

export interface FetchAllEtudiantsBisResult {
	results: any[]
	count: number
	truncated: boolean
	pagesFetched: number
	nb_licence?: number
	nb_master?: number
}

/**
 * Agrège toutes les pages d'une requête etudiants_bis.
 * maxPages=0 → illimité (suit `next` jusqu'à épuisement). Aucun plafond type 9999.
 */
export async function fetchAllEtudiantsBisPages(
	fetchPage: EtudiantsBisFetcher,
	baseParams: Record<string, unknown>,
	options: FetchAllEtudiantsBisOptions = {}
): Promise<FetchAllEtudiantsBisResult> {
	const perPage = options.perPage ?? ETUDIANTS_BIS_PAGE_SIZE
	const maxPages = options.maxPages ?? 0
	const useloading = options.useloading ?? false

	const all: any[] = []
	let page = 1
	let pagesFetched = 0
	let totalCount: number | null = null
	let lastNext: string | null | undefined = null
	let nbLicence: number | undefined
	let nbMaster: number | undefined

	// Ignorer un éventuel limit héritée (ex. 9999) : la taille de page = perPage (100)
	const { limit: _ignoredLimit, ...safeBaseParams } = baseParams

	while (true) {
		const response = await fetchPage(
			{
				...safeBaseParams,
				page,
				limit: perPage,
				per_page: perPage,
				page_size: perPage,
			},
			useloading && page === 1
		)

		const batch = Array.isArray(response?.results) ? response.results : []
		all.push(...batch)
		pagesFetched++

		if (totalCount === null && typeof response?.count === 'number') {
			totalCount = response.count
		}
		if (typeof response?.nb_licence === 'number') nbLicence = response.nb_licence
		if (typeof response?.nb_master === 'number') nbMaster = response.nb_master

		lastNext = response?.next ?? null

		// Garde-fou: si l'API renvoie next mais une page vide (ou cassée), éviter une boucle infinie.
		if (batch.length === 0) break

		if (!lastNext) break
		if (totalCount !== null && all.length >= totalCount) {
			lastNext = null
			break
		}
		if (maxPages > 0 && pagesFetched >= maxPages) break

		page++
	}

	const truncated =
		(totalCount !== null && all.length < totalCount) ||
		(!!lastNext && (maxPages === 0 || pagesFetched >= maxPages))

	return {
		results: all,
		count: totalCount ?? all.length,
		truncated,
		pagesFetched,
		nb_licence: nbLicence,
		nb_master: nbMaster,
	}
}

/**
 * @param fetchPage fetcher silencieux (ex. fetchEtudiantsBisPageSilent) — ne doit pas muter le store.
 */
export async function fetchAllEtudiantsBisForSessionSpecialite(
	fetchPage: EtudiantsBisFetcher,
	sessionId: number,
	specialiteId: number,
	options?: FetchAllEtudiantsBisOptions
): Promise<FetchAllEtudiantsBisResult> {
	return fetchAllEtudiantsBisPages(
		fetchPage,
		{ session: sessionId, specialite: specialiteId },
		options
	)
}
